import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Table } from 'src/design/molecules/Table';
import React from 'react';
import { DailyProgressItemDto } from 'src/api/FiretrolApiClient';
import { useDailyDesignProgressSummary } from 'src/api/queries/dailys/useDailyDesignProgressSummary';
import { useJobWbs } from 'src/api/queries/wbs/useJobWbs';
import { convertAccountsToTreeData, findNodeInTree } from 'src/utils';
import { CTCLinkIndicator } from 'src/routes/Jobs/WBS/components/CTCLinkIndicator';

interface ProgressTabProps {
  jobId: number;
  dailyId?: string;
  progressItems: DailyProgressItemDto[];
}

export const ProgressTab: React.FC<ProgressTabProps> = ({
  jobId,
  dailyId,
  progressItems,
}) => {
  const { data: wbsData, isLoading } = useJobWbs(jobId);
  const designProgressRequest = React.useMemo(() => ({ dailyId }), [dailyId]);
  const { data: designProgressSummary = [], isLoading: designProgressLoading } =
    useDailyDesignProgressSummary(jobId, designProgressRequest);
  const wbs = React.useMemo(
    () => (wbsData ? convertAccountsToTreeData(wbsData.wbsAccount) : []),
    [wbsData],
  );

  const rows = [
    ...progressItems.map((w) => ({
      wbsAccountId: w.wbsAccountId,
      value: w.value,
      fromViewer: false,
    })),
    ...designProgressSummary.map((s) => ({
      wbsAccountId: s.wbsAccountId,
      value: s.quantity,
      fromViewer: true,
    })),
  ];

  const renderRows = () => {
    return rows.map((w) => {
      const node = findNodeInTree(wbs, w.wbsAccountId);
      if (!node) return null;
      return (
        <Table.Tr
          key={`${w.fromViewer ? 'viewer' : 'manual'}-${w.wbsAccountId}`}
        >
          <Table.Td>
            <Group gap="xs" align="center">
              {node.name}
              <CTCLinkIndicator node={node} size="xs" />
              {w.fromViewer && (
                <Badge size="xs" variant="light">
                  Progress viewer
                </Badge>
              )}
            </Group>
          </Table.Td>
          <Table.Td>
            {Number(node.currentQuantityEstimate)?.toFixed(0) || '--'}
          </Table.Td>
          <Table.Td>{w?.value?.toFixed(0) || 0}</Table.Td>
        </Table.Tr>
      );
    });
  };
  if (isLoading || (!!dailyId && designProgressLoading)) {
    return (
      <Stack align="center" mt="md">
        <Loader />
      </Stack>
    );
  }
  if (wbs.length === 0) {
    return (
      <Text>
        No active charge codes. Update charge codes in the job WBS settings.
      </Text>
    );
  }
  return (
    <>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Charge Code</Table.Th>
            <Table.Th>Estimated Quantity</Table.Th>
            <Table.Th>Installed Quantity</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{renderRows()}</Table.Tbody>
      </Table>
    </>
  );
};
